import { z } from 'zod';

import { baseOrganizationEntitySchema } from './organizationContract';
import {
  ProductAvailabilityStatus,
  ShopAttributeDataType,
  ShopProductAttributeSource,
  ShopProductContentSource,
  ShopProductStatus,
} from '../enums';

export const shopProductDeliverySchema = z.object({
  carrierId: z.string(),
  price: z.number().nullish(),
  priceCod: z.number().nullish(),
});

export const shopProductSalesVoucherSchema = z.object({
  code: z.string(),
  description: z.string(),
});

/**
 * Mirrors the constraints the feed parser already enforces, so a warranty that
 * survived parsing cannot be rejected here: a zero-month warranty and an
 * unlabelled one are both parse failures rather than data.
 */
export const shopProductExtendedWarrantySchema = z.object({
  months: z.number().int().positive(),
  description: z.string().min(1),
});

export const ShopProductSchema = baseOrganizationEntitySchema.extend({
  feed: z.string().uuid(),
  name: z.string(),
  title: z.string().nullish(),
  description: z.string().nullish(),
  active: z.boolean(),
  externalId: z.string(),
  variantGroupId: z.string().nullish(),
  url: z.string(),
  imageUrl: z.string().nullish(),
  images: z.array(z.string()).nullish(),
  price: z.number().nullish(),
  priceBeforeDiscount: z.number().nullish(),
  salePrice: z.number().nullish(),
  vat: z.number().nullish(),
  currency: z.string(),
  availability: z.nativeEnum(ProductAvailabilityStatus),
  brand: z.string().nullish(),
  type: z.string().nullish(),
  ean: z.string().nullish(),
  isbn: z.string().nullish(),
  gifts: z.array(z.string()).nullish(),
  salesVouchers: z.array(shopProductSalesVoucherSchema).nullish(),
  extendedWarranty: shopProductExtendedWarrantySchema.nullish(),
  specialServices: z.array(z.string()).nullish(),
  stockQuantity: z.number().int().nullish(),
  deliveryDate: z.coerce.date().nullish(),
  dispatchLeadTimeDays: z.number().int().nonnegative().nullish(),
  availabilityDate: z.coerce.date().nullish(),
  delivery: z.array(shopProductDeliverySchema).nullish(),
  sourceMetadata: z.record(z.unknown()).nullish(),
  scheduledAt: z.coerce.date(),
  status: z.nativeEnum(ShopProductStatus),
  startedEmbeddingAt: z.coerce.date().nullish(),
  finishedEmbeddingAt: z.coerce.date().nullish(),
  lastError: z.string().nullish(),
  contentHash: z.string().nullish(),
  lastSeenAt: z.coerce.date().nullish(),
  offerHash: z.string().nullish(),
  descriptiveHash: z.string().nullish(),
  offerUpdatedAt: z.coerce.date().nullish(),
  contentUpdatedAt: z.coerce.date().nullish(),
  pageContent: z.string().nullish(),
  contentSource: z.nativeEnum(ShopProductContentSource).nullish(),
  shortSummary: z.string().nullish(),
  longSummary: z.string().nullish(),
  searchTerms: z.array(z.string()).nullish(),
  imageAnnotations: z.array(z.string()).nullish(),
  enrichmentContentHash: z.string().nullish(),
  enrichedAt: z.coerce.date().nullish(),
  enrichmentSnapshot: z
    .object({ key: z.string(), description: z.string() })
    .nullish(),
  embeddingSnapshot: z
    .object({
      format: z.string(),
      objectId: z.string(),
      identity: z.string(),
      discovery: z.string(),
      description: z.string(),
    })
    .nullish(),
});

/**
 * Fields owned exclusively by the import and enrichment pipeline: scraped page
 * markdown, model-generated output, and the hashes and run stamps that decide
 * whether either step may be skipped for a product.
 *
 * They are readable on {@link ShopProductSchema} but stripped from both write
 * schemas — a hand-written hash would make the importer skip a product
 * permanently, and a hand-written `lastSeenAt` would hide a product that has
 * genuinely disappeared from the feed.
 */
const pipelineOwnedFields = {
  contentHash: true,
  lastSeenAt: true,
  offerHash: true,
  descriptiveHash: true,
  offerUpdatedAt: true,
  contentUpdatedAt: true,
  pageContent: true,
  contentSource: true,
  shortSummary: true,
  longSummary: true,
  searchTerms: true,
  imageAnnotations: true,
  enrichmentContentHash: true,
  enrichedAt: true,
  enrichmentSnapshot: true,
  embeddingSnapshot: true,
} as const;

export const createShopProductSchema = ShopProductSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  organization: true,
  status: true,
  startedEmbeddingAt: true,
  finishedEmbeddingAt: true,
  scheduledAt: true,
  lastError: true,
  ...pipelineOwnedFields,
}).strict();

export const updateShopProductSchema = ShopProductSchema.partial()
  .required({
    id: true,
  })
  .omit({
    organization: true,
    feed: true,
    externalId: true,
    ...pipelineOwnedFields,
  })
  .strict();

export type ShopProductDTO = z.infer<typeof ShopProductSchema>;
export type CreateShopProductDTO = z.infer<typeof createShopProductSchema>;
export type UpdateShopProductDTO = z.infer<typeof updateShopProductSchema>;

export const ShopProductAttributeSchema = baseOrganizationEntitySchema.extend({
  feed: z.string().uuid(),
  name: z.string().min(1),
  displayName: z.string().nullish(),
  aliases: z.array(z.string()).nullish(),
  filterable: z.boolean(),
  active: z.boolean(),
  dataType: z.nativeEnum(ShopAttributeDataType),
  unit: z.string().nullish(),
  baseUnit: z.string().nullish(),
});

/**
 * The typing contract, decided when the attribute is first created and relied
 * on by every stored `numericValue` from then on.
 *
 * Writable at creation ({@link createShopProductAttributeSchema}) and never
 * again: changing `baseUnit` alone would leave the already-normalized numbers
 * on disk measured in the old unit, silently comparing 23 against 23 000 in
 * the next range query. Retyping an existing attribute is a re-enrichment, not
 * a form field — which is also why the create path resolves a name collision
 * onto the existing row under *its* typing rather than honouring the typing
 * that was submitted with it.
 *
 * One exception is not an exception: `Text` with no base unit is the
 * *undecided* state, the same one the importer writes a merchant's parameter
 * as, and enrichment may raise it to a real typing once
 * (`ShopProductEnrichmentService#upgradeUntypedAttribute`). A hand-created
 * text attribute therefore starts undecided and can still become a filterable
 * number later, which is a feature rather than a hole: an untyped attribute
 * holds no `numericValue`, so no stored number's meaning can change under it.
 */
const attributeTypingFields = {
  dataType: true,
  unit: true,
  baseUnit: true,
} as const;

/**
 * An attribute definition an admin writes by hand, to hold a value neither the
 * feed nor the model produced for one product.
 *
 * Attributes were created only by the importer and the enrichment service,
 * which is why this file carried no create schema for them. Both still create
 * them — the admin is a third writer, not a replacement: adding a value to a
 * product may need a vocabulary entry that does not exist yet, and the row it
 * creates is an ordinary one. The definition has no provenance field and does
 * not want one; only the *value* records who wrote it, through
 * `ShopProductAttributeValue.source`, which is also the only thing that decides
 * which writer may touch a row later.
 *
 * `feed` and `organization` are absent because both are read from the product
 * the value is being added to, so a definition cannot land on a feed the admin
 * is not looking at. `filterable` and `active` are absent because a
 * hand-created attribute starts on and filterable, exactly as the enrichment
 * service creates one; switching either off is a feed-wide action of its own.
 */
export const createShopProductAttributeSchema = ShopProductAttributeSchema.omit(
  {
    id: true,
    createdAt: true,
    updatedAt: true,
    organization: true,
    feed: true,
    aliases: true,
    filterable: true,
    active: true,
  },
)
  .strict()
  .superRefine((attribute, ctx) => {
    const isNumber = attribute.dataType === ShopAttributeDataType.Number;

    /**
     * A number attribute without a base unit is stored but never comparable:
     * every `numericValue` under it is a bare number in no stated unit, so a
     * range filter can only put 500 next to 0.5 and hope. Ingest derives the
     * base unit from what it saw; a hand-created attribute has to be told.
     */
    if (isNumber && !attribute.baseUnit?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['baseUnit'],
        message:
          'A number attribute needs the unit its numbers are compared in',
      });
    }

    if (!isNumber && (attribute.unit?.trim() || attribute.baseUnit?.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unit'],
        message: 'Only a number attribute has units',
      });
    }
  });

export const updateShopProductAttributeSchema =
  ShopProductAttributeSchema.partial()
    .required({
      id: true,
    })
    .omit({
      organization: true,
      feed: true,
      ...attributeTypingFields,
    })
    .strict();

export const ShopProductAttributeValueSchema =
  baseOrganizationEntitySchema.extend({
    product: z.string().uuid(),
    attribute: z.string().uuid(),
    value: z.string(),
    numericValue: z.number().nullish(),
    normalizedValue: z.string().nullish(),
    source: z.nativeEnum(ShopProductAttributeSource),
    active: z.boolean(),
  });

/**
 * The diacritics-stripped facet key, folded from `value` at write time.
 * Mechanical, and nobody has a reason to hand-write one — accepting it from a
 * client could only make it disagree with the value it indexes, which no
 * reader can detect.
 *
 * `numericValue` deliberately stays writable. It is the number the whole
 * catalog is range-compared on, and nothing can re-derive it: the unit table
 * and quantity parser are gone, so `500 ml` on a litre-based attribute has no
 * mechanical path to `0.5`. Since enrichment never revisits a `Manual` row,
 * refusing the edit would strand a hand-corrected value outside every range
 * filter permanently. We already trust a model to produce this number from
 * the same text; a human saying so is not a weaker claim. There is
 * intentionally no consistency check against `value` — an override that the
 * system may overrule is not an override.
 */
const derivedAttributeValueFields = {
  normalizedValue: true,
} as const;

/**
 * A value an admin adds to one product, for an attribute the feed's
 * vocabulary already has.
 *
 * `source` and `active` are omitted rather than accepted: every row written
 * through this path is `Manual` and live, and forcing both server-side is what
 * makes a hand-written value survive both automatic writers — the importer
 * updates and sweeps only its own `Feed` rows, and enrichment rewrites what it
 * owns and stops at `Manual`. Letting a client name the source would make an
 * unprotected hand-written value representable, which is the one thing this
 * path must not allow.
 */
export const createShopProductAttributeValueSchema =
  ShopProductAttributeValueSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    organization: true,
    source: true,
    active: true,
    ...derivedAttributeValueFields,
  }).strict();

/**
 * The same, for an attribute the feed does not have yet: the definition and
 * the first value that justifies it, written together.
 *
 * They are one request because they are one decision. Creating the vocabulary
 * entry on its own would leave the feed carrying an attribute no product uses
 * — and if the value write then failed, the admin would be left with exactly
 * that and no way to see it from the product they were looking at.
 */
export const createShopProductAttributeWithValueSchema =
  createShopProductAttributeValueSchema
    .omit({ attribute: true })
    .extend({ attribute: createShopProductAttributeSchema })
    .strict();

export const updateShopProductAttributeValueSchema =
  ShopProductAttributeValueSchema.partial()
    .required({
      id: true,
    })
    .omit({
      organization: true,
      product: true,
      attribute: true,
      ...derivedAttributeValueFields,
    })
    .strict();

export type ShopProductAttributeDTO = z.infer<
  typeof ShopProductAttributeSchema
>;
export type CreateShopProductAttributeDTO = z.infer<
  typeof createShopProductAttributeSchema
>;
export type UpdateShopProductAttributeDTO = z.infer<
  typeof updateShopProductAttributeSchema
>;
export type ShopProductAttributeValueDTO = z.infer<
  typeof ShopProductAttributeValueSchema
>;
export type CreateShopProductAttributeValueDTO = z.infer<
  typeof createShopProductAttributeValueSchema
>;
export type CreateShopProductAttributeWithValueDTO = z.infer<
  typeof createShopProductAttributeWithValueSchema
>;
export type UpdateShopProductAttributeValueDTO = z.infer<
  typeof updateShopProductAttributeValueSchema
>;
